/**
 * Choosing an item on an invoice line.
 *
 * WHY THIS EXISTS
 * ---------------
 * The editor used to render one `<select>` per line, each carrying an `<option>`
 * for EVERY product. The fixture catalogue has 796 products, so a 40-line
 * invoice built roughly 31,800 `<option>` elements - and rebuilt them again on
 * every keystroke in every field of every line, because they sat in the same
 * render pass as the inputs being typed into.
 *
 * That is not a slow screen, it is an unusable one: adding lines took minutes,
 * and it got worse with every product the shop ever added. A shop with a few
 * thousand items could not raise an invoice at all.
 *
 * The fix has to keep a real `<select>`, not swap in a custom combobox: the
 * keyboard behaviour, the mobile picker, and the accessibility semantics of a
 * native select are all worth more than the flexibility, and the tests drive it
 * with `selectOption`. So the work is done by giving the native control less to
 * draw, which is what this module decides.
 *
 * Three rules, and each one exists because breaking it is a bug:
 *
 *  1. A per-line search box narrows that line only. A single shared box would be
 *     cheaper still, but it is unusable: pick a product on line 1 and the shared
 *     filter changes, so line 2's product vanishes from under you.
 *
 *  2. The list is CAPPED. Search narrows, but an empty search on a big catalogue
 *     is still the whole catalogue, so the cap is what actually bounds the DOM.
 *
 *  3. The chosen product is ALWAYS in the list, even when it does not match the
 *     search and even when the cap would have cut it. Otherwise the select holds
 *     a value it cannot display and renders blank - the line silently appears
 *     empty, which looks like data loss to the person filling the invoice in.
 */
import type { ProductListItem } from '@data/services/productReader';

/**
 * How many products one line may show at once.
 *
 * 40 lines at 100 is 4,000 options, against 31,800 before: an eighth of the
 * work, and - more to the point - a number that no longer grows when the shop
 * adds products. A catalogue of 5,000 renders exactly as fast as one of 796.
 *
 * High enough that a shopkeeper who knows roughly what they want sees it
 * without typing, low enough that a line stays instant.
 */
export const ITEM_OPTION_LIMIT = 100;

/** What the search box is compared against. Lower-cased and trimmed. */
export function normaliseQuery(query: string): string {
  return query.trim().toLowerCase();
}

/**
 * Whether one product matches a search.
 *
 * Matches the name and the SKU. The SKU is deliberately included: a shopkeeper
 * who knows the code they wrote on the shelf label is looking the product up by
 * a string they can read off the goods, not by a name they are trying to recall.
 *
 * The needle is normalised HERE rather than trusted from the caller. This is
 * exported, so someone will call it with whatever the user typed - and a
 * function that returns `false` for `'CEMENT'` because it expected `'cement'`
 * is a silent wrong answer, not a crash. `pickerOptions` still normalises once
 * up front so the hot path pays for one fold rather than one per product.
 */
export function matchesQuery(item: ProductListItem, rawNeedle: string): boolean {
  const needle = normaliseQuery(rawNeedle);
  if (needle === '') return true;
  const p = item.product;
  return (
    p.name.toLowerCase().includes(needle) || p.sku.toLowerCase().includes(needle)
  );
}

export interface PickerOptions {
  /** The products to render, already capped, and never empty of the chosen one. */
  readonly options: readonly ProductListItem[];
  /** True when products were dropped by the cap rather than by the search. */
  readonly truncated: boolean;
  /** How many products matched the search before the cap was applied. */
  readonly matched: number;
}

/**
 * The products one line should offer.
 *
 * `selectedId` is the line's current choice, and it is honoured ABSOLUTELY: if
 * the chosen product is missing from the list the `<select>` holds a value it
 * cannot display, and the line renders blank. To the person filling the invoice
 * in that reads as data loss, not as a filter, and they will "fix" it by picking
 * something else.
 *
 * So the chosen product is looked up in the FULL catalogue, not in the search
 * results - otherwise searching for something else makes the line appear empty
 * while it still holds its real value.
 */
export function pickerOptions(
  items: readonly ProductListItem[],
  query: string,
  selectedId: string,
): PickerOptions {
  const needle = normaliseQuery(query);

  // An empty search is the common case - every untouched line - and scanning
  // 796 products for it is pure waste. The cap alone is enough.
  const matched =
    needle === ''
      ? items
      : items.filter((item) =>
          item.product.name.toLowerCase().includes(needle) ||
          item.product.sku.toLowerCase().includes(needle),
        );

  // The cap is applied FIRST, and the chosen product is then checked against the
  // CAPPED list.
  //
  // The order matters, and reversing it is a silent bug: checking against the
  // uncapped matches finds the chosen product (it is in the catalogue, of
  // course) and concludes there is nothing to do - and then the cap drops it
  // anyway. The line renders blank while still holding the product.
  const capped =
    matched.length <= ITEM_OPTION_LIMIT
      ? matched
      : matched.slice(0, ITEM_OPTION_LIMIT);
  const truncated = matched.length > ITEM_OPTION_LIMIT;

  if (selectedId === '' || capped.some((item) => item.product.id === selectedId)) {
    return { options: capped, truncated, matched: matched.length };
  }

  // The full catalogue, not the search results: searching for something else
  // must not make the line appear empty while it still holds its real value.
  const chosen = items.find((item) => item.product.id === selectedId);
  // A chosen id that is not in the catalogue at all (the row was deleted while
  // the form was open) is NOT manufactured into an option. `lineProblem` is what
  // reports that to the user, and inventing an option would hide it.
  if (!chosen) return { options: capped, truncated, matched: matched.length };

  const room = ITEM_OPTION_LIMIT - capped.length;
  return {
    // Append when there is room; otherwise SWAP the last one out. Appending into
    // a full list would render ITEM_OPTION_LIMIT + 1 options, and the cap would
    // quietly stop being a cap.
    options: room > 0 ? [...capped, chosen] : [...capped.slice(0, -1), chosen],
    truncated,
    matched: matched.length,
  };
}